/**
 * SecureEmbed Energy Portal - Sub-Role Selector
 *
 * Horizontal button bar for selecting an energy-sector sub-role.
 * Selected sub-role shows filled/highlighted style; unselected shows outlined/ghost.
 * Addresses bug #288 — persona sub-role active state.
 */

import React from 'react';
import {
  TrendingUp, Activity, Calculator, Zap, Network, GitBranch,
  ShieldCheck, FileText, BookOpen, Leaf, BarChart3, ClipboardList,
} from 'lucide-react';
import { SUB_ROLE_GROUPS, SUB_ROLES, type SubRoleDefinition } from '../constants/personas';
import { usePersona } from '../contexts/PersonaContext';

// Map icon names to Lucide components
const ICON_MAP: Record<string, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  TrendingUp, Activity, Calculator, Zap, Network, GitBranch,
  ShieldCheck, FileText, BookOpen, Leaf, BarChart3, ClipboardList,
};

interface SubRoleSelectorProps {
  /** Optional: filter to show only one group's sub-roles */
  group?: string;
}

const SubRoleSelector: React.FC<SubRoleSelectorProps> = ({ group }) => {
  const { selectedSubRole, setSubRole, clearSubRole } = usePersona();

  const groups = group
    ? SUB_ROLE_GROUPS.filter((g) => g.key === group)
    : SUB_ROLE_GROUPS;

  const handleClick = (key: string) => {
    if (selectedSubRole === key) {
      clearSubRole();
    } else {
      setSubRole(key);
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3">
      <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
        {groups.map((grp) => {
          const subRoles = grp.subRoles
            .map((k) => SUB_ROLES[k])
            .filter(Boolean);

          return (
            <div key={grp.key} className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                {grp.label}
              </span>
              <div className="flex items-center gap-1.5">
                {subRoles.map((sr: SubRoleDefinition) => {
                  const Icon = ICON_MAP[sr.iconName] || TrendingUp;
                  const isSelected = selectedSubRole === sr.key;

                  return (
                    <button
                      key={sr.key}
                      onClick={() => handleClick(sr.key)}
                      title={sr.description}
                      className={`
                        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                        transition-all duration-200 whitespace-nowrap
                        ${isSelected
                          ? 'text-white shadow-md ring-2 ring-offset-2 font-semibold'
                          : 'bg-transparent border hover:shadow-sm'
                        }
                      `}
                      style={isSelected
                        ? {
                            backgroundColor: sr.color,
                            boxShadow: `0 4px 6px -1px ${sr.color}33, 0 2px 4px -2px ${sr.color}22`,
                            outlineColor: sr.color,
                            outlineOffset: '2px',
                            outline: `2px solid ${sr.color}`,
                          }
                        : {
                            borderColor: sr.color,
                            color: sr.color,
                          }
                      }
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          (e.currentTarget as HTMLElement).style.backgroundColor = `${sr.color}10`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {sr.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SubRoleSelector;
